import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from '../../PlatformFunctionality';
import { useTrackingContext } from '../../Tracking';
import { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { useSuggestedProducts } from '../../hooks/useSuggestedProducts';
import { getStoreCurrency } from '@jbhi-fi/utils/src/shopify/getStoreCurrency';
import { transformAlgoliaHitForTracking } from '../../Algolia/transformers/transformAlgoliaHitForTracking';
import { trackViewItemList } from '@jbhi-fi/utils/src/ga4Tracking/functions/trackViewItemList';
import { useSmartModuleTracking } from '../../hooks/useSmartModuleTracking/useSmartModuleTracking';

interface AlternativesContextState {
  currentProduct?: AlgoliaProductHit;
  alternatives?: Array<AlgoliaProductHit>;
  isSlideoutOpen: boolean;
  isLoading: boolean;
  slideoutType: 'similarItems' | 'alternatives';
  numberOfProducts: number;
}

export interface AlternativesContextProviderInterface extends AlternativesContextState {
  setState: React.Dispatch<React.SetStateAction<AlternativesContextState>>;
}

type AlternativesContextProviderProps = {
  children: React.ReactNode;
  value?: AlternativesContextProviderInterface;
  alternativesGoogleRecAiModel?: string;
  requestedProductsCount?: number;
};

const AlternativesContext = createContext<AlternativesContextProviderInterface | null>(null);

export const AlternativesContextProvider = ({
  value,
  children,
  alternativesGoogleRecAiModel,
}: AlternativesContextProviderProps) => {
  const [state, setState] = useState<AlternativesContextState>({
    currentProduct: value?.currentProduct,
    isSlideoutOpen: value?.isSlideoutOpen ?? false,
    slideoutType: value?.slideoutType ?? 'alternatives',
    alternatives: value?.alternatives || [],
    isLoading: value?.isLoading ?? false,
    numberOfProducts: value?.numberOfProducts || 12,
  });

  const { suggestions, isLoading } = useSuggestedProducts({
    products: [state.currentProduct],
    isEnabled: state.isSlideoutOpen && state.currentProduct && alternativesGoogleRecAiModel !== undefined,
    googleRecModel: alternativesGoogleRecAiModel || 'SIMILAR',
    googleRecNumProducts: state.numberOfProducts,
    isAttachSuggestionEnabled: false,
    shouldFetchImsSuggestions: false,
  });

  useEffect(() => {
    const alternatives = state.alternatives?.length ? state.alternatives : suggestions;
    if (!state.isSlideoutOpen || !alternatives?.length) return;
    const listName = state.slideoutType === 'alternatives' ? 'Browse alternatives' : 'Similar items';
    trackViewItemList({
      currency: getStoreCurrency(),
      listName,
      itemListSlot: 'slideout',
      itemListServiceConfig: alternativesGoogleRecAiModel,
      viewedItems: transformAlgoliaHitForTracking(alternatives),
    });
  }, [state.isSlideoutOpen, state.alternatives, suggestions, state.slideoutType, alternativesGoogleRecAiModel]);

  return (
    <AlternativesContext.Provider
      value={{
        ...value,
        ...state,
        alternatives: state.alternatives?.length ? state.alternatives : suggestions,
        isLoading: value?.isLoading || isLoading,
        setState,
      }}
    >
      {children}
    </AlternativesContext.Provider>
  );
};

export const useAlternativesContext = () => {
  const context = useContext(AlternativesContext);

  const navigate = useNavigate();

  const { tracker } = useTrackingContext();
  const { trackSmartModuleEvent } = useSmartModuleTracking();

  if (context === null) {
    throw new Error('useAlternativesContext must be used within a AlternativesContextProvider');
  }

  const { alternatives, currentProduct, isSlideoutOpen, isLoading, slideoutType, numberOfProducts, setState } = context;

  const handleReviewCartClick = () => {
    tracker.trackClick({ label: 'Review cart', source: 'alternatives' });
    navigate('/cart');
  };

  const hanleContinueShoppingClick = () => {
    tracker.trackClick({ label: 'Continue shopping', source: 'alternatives' });
    setState(prev => ({ ...prev, isSlideoutOpen: false }));
  };

  const handleCloseSlideout = () => {
    tracker.trackClick({ label: 'Slideout close', source: 'alternatives' });
    setState(prev => ({
      ...prev,
      isSlideoutOpen: false,
      currentProduct: undefined,
    }));
  };

  const handleViewAlternatives = (
    product: AlgoliaProductHit,
    numberOfProducts: number,
    slideoutType: 'similarItems' | 'alternatives' = 'alternatives',
    ecommerceExtraProps?: { list?: string; itemListSlot?: string; itemListServiceConfig?: string }
  ) => {
    const isSimilarItems = slideoutType === 'similarItems';
    const { list, itemListSlot, itemListServiceConfig } = ecommerceExtraProps || {};
    trackSmartModuleEvent({
      product,
      event: isSimilarItems ? 'click_similar_items' : 'click_view_alternative',
      item_list_name: isSimilarItems ? 'Similar items' : list,
      item_list_slot: isSimilarItems ? 'slideout' : itemListSlot,
      item_list_service_config: itemListServiceConfig,
    });
    setState(prev => ({
      ...prev,
      isSlideoutOpen: !isSlideoutOpen,
      currentProduct: product,
      slideoutType,
      numberOfProducts,
    }));
  };

  return {
    alternatives: alternatives?.slice(0, numberOfProducts) || [],
    isSlideoutOpen,
    isLoading,
    currentProduct,
    slideoutType,
    handleCloseSlideout,
    handleReviewCartClick,
    hanleContinueShoppingClick,
    handleViewAlternatives,
  };
};
